"use client"

import { useEffect, useRef, useState } from "react"
import {
  A11y,
  Autoplay,
  EffectFade,
  Keyboard,
  Swiper as SwiperClass,
} from "swiper"
import { SwiperSlide } from "swiper/react"
import HeroNav from "./HeroNav"
import HeroSlide from "./HeroSlide"
import { HeroSection, HeroSwiper } from "./hero.styled"
import { resolveSlideDurationSec } from "./heroHelpers"
import { HeroCarouselProps } from "./types"

// The fallback autoplay timer sits this far past the video's own end, so the
// `ended` event always advances the slide first and the two never race. The
// timer only takes over if a video stalls or errors mid-play.
const VIDEO_END_GRACE_MS = 400

const HeroCarousel = ({ data }: HeroCarouselProps) => {
  const slides = data?.slides ?? []
  const total = slides.length
  const isMulti = total > 1

  const swiperRef = useRef<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [isPlaying, setIsPlaying] = useState(true)
  // Length of each slide's visible background video, keyed by slide index.
  // Populated from loadedmetadata, which fires well before the slide goes active.
  const [videoDurations, setVideoDurations] = useState<Record<number, number>>(
    {}
  )

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const apply = () => {
      setReducedMotion(mq.matches)
      if (mq.matches) setIsPlaying(false)
    }
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])

  const autoDelaySec = data?.autoPlayInterval ?? 7
  const autoDelay = autoDelaySec * 1000
  // A slide lasts exactly as long as its background video — stretching past the
  // CMS interval as well as cutting under it. Image slides keep the interval
  // (DLK-949). Also drives the nav dot's progress bar.
  const activeSlideDurationSec = resolveSlideDurationSec(
    videoDurations[activeIndex],
    autoDelaySec
  )

  // Swiper schedules each autoplay timeout once, when the slide becomes active —
  // so the very first slide is scheduled before its video has reported a
  // duration, and would keep the stale interval for that first pass. Re-arm the
  // timer once the duration lands. Guarded on `running` so a carousel the user
  // paused is never resurrected.
  useEffect(() => {
    const sw = swiperRef.current
    if (!sw?.autoplay?.running) return
    sw.autoplay.stop()
    sw.autoplay.start()
  }, [activeSlideDurationSec])

  if (total === 0) return null

  const activeSlide = slides[activeIndex]
  const hasActiveVideo = !!activeSlide?.backgroundVideo?.imageUrl
  // Single slide: hide dots/counter; keep toggle only if that slide has a video (WCAG 2.2.2)
  const showDots = isMulti
  const showToggle = (isMulti || hasActiveVideo) && !reducedMotion

  const handleToggle = () => {
    const sw = swiperRef.current
    setIsPlaying((prev) => {
      const next = !prev
      if (sw?.autoplay) {
        if (next) sw.autoplay.start()
        else sw.autoplay.stop()
      }
      return next
    })
  }
  const goTo = (i: number) => {
    swiperRef.current?.slideTo?.(i)
  }
  // The video's own end is what advances a video slide — exact, and immune to
  // drift between wall-clock and playback (DLK-949). The per-slide autoplay
  // delay below is only the fallback for a video that stalls or errors out.
  // `rewind` is enabled, so slideNext() wraps from the last slide to the first.
  const handleVideoEnded = () => {
    if (!isMulti) return
    swiperRef.current?.slideNext()
  }
  const handleVideoDuration = (index: number, seconds: number) => {
    setVideoDurations((prev) =>
      prev[index] === seconds ? prev : { ...prev, [index]: seconds }
    )
  }
  // Per-slide override of the autoplay delay, so a video longer than the CMS
  // interval is not cut off. Undefined omits the attribute and Swiper falls back
  // to params.autoplay.delay — which is what image slides want.
  const slideDelayMs = (index: number) => {
    const videoSec = videoDurations[index]
    if (!videoSec || !Number.isFinite(videoSec)) return undefined
    return Math.round(videoSec * 1000) + VIDEO_END_GRACE_MS
  }

  return (
    <HeroSection
      role="region"
      aria-roledescription="carousel"
      aria-label="מבצעים"
      dir="rtl"
    >
      <HeroSwiper
        modules={[Autoplay, EffectFade, A11y, Keyboard]}
        effect="fade"
        speed={900}
        fadeEffect={{ crossFade: true }}
        keyboard={{ enabled: isMulti }}
        rewind={isMulti}
        allowTouchMove={isMulti}
        autoplay={
          isMulti && !reducedMotion
            ? { delay: autoDelay, disableOnInteraction: false }
            : false
        }
        onSwiper={(sw) => {
          swiperRef.current = sw
        }}
        onSlideChange={(sw) => setActiveIndex(sw.realIndex)}
      >
        {slides.map((slide, i) => (
          <SwiperSlide key={slide.id} data-swiper-autoplay={slideDelayMs(i)}>
            {({ isActive }) => (
              <HeroSlide
                slide={slide}
                index={i}
                total={total}
                isActive={isActive}
                shouldPlayVideo={isActive && isPlaying && !reducedMotion}
                reducedMotion={reducedMotion}
                canAdvance={isMulti}
                onVideoEnded={handleVideoEnded}
                onVideoDuration={(seconds) => handleVideoDuration(i, seconds)}
              />
            )}
          </SwiperSlide>
        ))}
        <div slot="container-end">
          <HeroNav
            total={total}
            activeIndex={activeIndex}
            isPlaying={isPlaying}
            autoDelaySec={activeSlideDurationSec}
            showDots={showDots}
            showToggle={showToggle}
            onToggle={handleToggle}
            goTo={goTo}
          />
        </div>
      </HeroSwiper>
    </HeroSection>
  )
}

export default HeroCarousel
